import { useState } from 'react';
import { Button, Card, Chip, IconButton, Link, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useCanAccess } from 'react-admin';
import { Link as RouterLink } from 'react-router-dom';

import { DeleteProjectDialog } from '@/modules/projects/DeleteProjectDialog';
import { defaultTypeOfWork } from '@/root/defaultConfiguration';
import { QuoteJobFolder } from '@/types';
import { formatDate } from '@/utils/date_utils';

function formatJobFolderAddress(record: QuoteJobFolder): string {
    const parts: string[] = [];
    if (record.address?.trim()) {
        parts.push(record.address.trim());
    }
    if (record.city && record.state) {
        parts.push(`${record.city} - ${record.state}`);
    } else if (record.city) {
        parts.push(record.city);
    } else if (record.state) {
        parts.push(record.state);
    }
    let line = parts.join(', ');
    if (record.zip_code) {
        line = `${line} ${record.zip_code}`.trim();
    }
    return line;
}

const JobFolderDetail = ({
    label,
    value,
}: {
    label: string;
    value: string | number;
}) => (
    <Typography variant="body1">
        <Typography component="span" fontWeight={600}>
            {label}:{' '}
        </Typography>
        {value}
    </Typography>
);

export const JobFolderHeader = ({
    folder,
    listPath,
    onEditProjectNumber,
    onEditContacts,
    onCompleteProject,
    onReopenQuoting,
}: {
    folder: QuoteJobFolder;
    listPath: string;
    onEditProjectNumber?: () => void;
    onEditContacts?: () => void;
    onCompleteProject?: () => void;
    onReopenQuoting?: () => void;
}) => {
    const { canAccess: canDelete } = useCanAccess({
        resource: 'projects',
        action: 'delete',
    });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const showDelete = Boolean(canDelete);
    const showComplete = Boolean(onCompleteProject);
    const showReopenQuoting = Boolean(onReopenQuoting);
    const showDates = Boolean(folder.created_at || folder.updated_at);
    const projectLabel =
        folder.project_no != null
            ? `project #${folder.project_no}`
            : folder.name?.trim() || `project #${folder.id}`;

    return (
        <Card sx={{ p: 2 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                justifyContent="space-between"
                gap={2}
            >
                <Stack gap={0.75} sx={{ minWidth: 0, flex: 1 }}>
                    {folder.has_legacy && (
                        <Chip
                            label="Legacy"
                            size="small"
                            color="warning"
                            sx={{ alignSelf: 'flex-start' }}
                        />
                    )}
                    <JobFolderDetail label="Address" value={formatJobFolderAddress(folder)} />
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexWrap: 'wrap' }}>
                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                            <Typography component="span" fontWeight={600}>
                                Owner:{' '}
                            </Typography>
                            {folder.property_owner_id ? (
                                <Link
                                    component={RouterLink}
                                    to={`/contractor_owners/${folder.property_owner_id}/show`}
                                    underline="hover"
                                >
                                    {folder.property_owner_name?.trim() ||
                                        `Owner #${folder.property_owner_id}`}
                                </Link>
                            ) : (
                                (folder.property_owner_name ?? '—')
                            )}
                        </Typography>
                        {onEditContacts && (
                            <IconButton
                                size="small"
                                aria-label="Transfer owner and contractor"
                                onClick={onEditContacts}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                        <Typography component="span" fontWeight={600}>
                            Contractor:{' '}
                        </Typography>
                        {folder.contractor_id ? (
                            <Link
                                component={RouterLink}
                                to={`/contractor_owners/${folder.contractor_id}/show`}
                                underline="hover"
                            >
                                {folder.contractor_name?.trim() ||
                                    `Contractor #${folder.contractor_id}`}
                            </Link>
                        ) : (
                            (folder.contractor_name ?? '—')
                        )}
                    </Typography>
                    {folder.lead_id != null && (
                        <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                            <Typography component="span" fontWeight={600}>
                                Lead:{' '}
                            </Typography>
                            <Link
                                component={RouterLink}
                                to={`/leads/${folder.lead_id}/show`}
                                underline="hover"
                            >
                                {folder.lead_name?.trim() || `Lead #${folder.lead_id}`}
                            </Link>
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        <JobFolderDetail label="Project Number" value={folder.project_no ?? '—'} />
                        {onEditProjectNumber && folder.project_no != null && !folder.has_legacy && (
                            <IconButton
                                size="small"
                                aria-label="Edit project number"
                                onClick={onEditProjectNumber}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Stack>
                    {folder.folio_number && (
                        <JobFolderDetail label="Folio" value={folder.folio_number} />
                    )}
                    {folder.type_of_work && (
                        <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                            {defaultTypeOfWork[folder.type_of_work] ?? folder.type_of_work}
                        </Typography>
                    )}
                </Stack>
                {(showDelete || showComplete || showReopenQuoting || showDates) && (
                    <Stack
                        gap={0.5}
                        sx={{
                            flexShrink: 0,
                            textAlign: { xs: 'left', sm: 'right' },
                            alignItems: { xs: 'stretch', sm: 'flex-end' },
                        }}
                    >
                        {showComplete && (
                            <Button
                                size="small"
                                color="success"
                                variant="contained"
                                onClick={onCompleteProject}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}
                            >
                                Complete project
                            </Button>
                        )}
                        {showReopenQuoting && (
                            <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                onClick={onReopenQuoting}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}
                            >
                                Move back to Quotes
                            </Button>
                        )}
                        {showDelete && (
                            <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                onClick={() => setDeleteOpen(true)}
                                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}
                            >
                                Delete project
                            </Button>
                        )}
                        {folder.created_at && (
                            <Typography variant="body2" color="text.secondary">
                                <Typography component="span" fontWeight={600} color="text.primary">
                                    Created at:{' '}
                                </Typography>
                                {formatDate(folder.created_at, 'HH:mm MM-dd-yyyy')}
                            </Typography>
                        )}
                        {folder.updated_at && (
                            <Typography variant="body2" color="text.secondary">
                                <Typography component="span" fontWeight={600} color="text.primary">
                                    Last update:{' '}
                                </Typography>
                                {formatDate(folder.updated_at, 'HH:mm MM-dd-yyyy')}
                            </Typography>
                        )}
                    </Stack>
                )}
            </Stack>
            <DeleteProjectDialog
                open={deleteOpen}
                projectId={Number(folder.id)}
                projectLabel={projectLabel}
                listPath={listPath}
                onClose={() => setDeleteOpen(false)}
            />
        </Card>
    );
};
